import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, NavLink, useLocation } from 'react-router-dom';
import './App.css';
import './SRC_colours_oneFIle.css';
import './i18n/i18n';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Col, Container, Modal, Row } from 'react-bootstrap';
import { useAppSelector, useAppDispatch } from './app/hooks';

// images
import srcLogo from './icons/srcnet-dark.svg';
import srcLightLogo from './icons/srcnet-light.svg';
import magnifierWhiteIcon from './icons/magnifier-white.512.png';
import logsIcon from './icons/log.svg';

// constants
import { TITLE } from './utils/common/constants';

// types
import {
  APIStatusItem,
  CanfarServiceType,
  ComputeServiceType,
  DataItemType,
  FileBrowserPage,
  GatewayLogType,
  NotificationType,
  RucioLocationsType,
  Site,
  Theme,
  ToolDescription,
  ToolType,
  toolTypeToPath,
} from './utils/types';

// functions
import { APIPrefix, findLanguageIndex } from './utils/functions';

// Auth
import { redirectToLoginURL } from './utils/auth';

// classes
import Home from './components/home/home';
import SearchCatalog from './components/search-catalog/search-catalog';
import SearchCatalogCAOM from './components/search-catalog-caom/search-catalog-caom';
import Tool from './tools/tool';
import UserControl from './components/user-control/user-control';
import ProjectPanel from './components/panels/project-panel';
import UserControlOIDCToken from './components/user-control/user-control-oidc-token';
import UserProfile from './components/user-control/user-profile';
import { ToolButtonType } from './tools/tool-button';
import ToolButton from './tools/tool-button';
import SearchCompute from './components/search-compute/search-compute';
import ComputeJobs from './components/compute-jobs/compute-jobs';
import DataManagement from './components/data-management/data-management';
import InteractiveSessions from './components/interactive-sessions/interactive-sessions';
import InteractiveSessionsWindow from './components/interactive-sessions/interactive-sessions-window';
import UserManagement from './components/user-management/user-management';
import JobStatus from './components/data-management/job-status';
import Preferences from './components/user-preferences/preferences';
import ViewNotifications from './components/user-control/view-notifications';
import SessionToast from './components/gateway-toast/session-toast';
import { Logs } from './components/logs/logs';
import NewSessionPanel from './components/home/new-compute-session/new-session-panel';
import { CustomLogsToast } from './components/gateway-toast/error-toast';
import { RouteChangeHandler } from './tools/route-change-listener';
import RoundButton from './tools/round-button';
import ProjectFiles from './components/project/project-files';

// SSE Handler.
import SSEHandler from './services/SSEService';

// types relating to events.
import {
  Event,
  EventType,
  EventTarget,
  AddCollectionEvent,
  InitiateComputeSearchParams,
  InitiateDMSearchParams,
  ServiceStatusParams,
  APIStatusParams,
} from './utils/events';
import AuthCallback from './services/AuthCallback';
import { getBrowserPreferredDarkMode } from './utils/common/constants';

// LEDA 2605481

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from './app/store';

// import state definition and functions.
import { loadDataProductTypes } from './components/search-catalog/search-catalog-actions';
import { setSessionsLoaded } from './components/interactive-sessions/interactive-sessions-slice';
import {
  refreshSRCNetServices,
  refreshCanfarServices,
} from './components/interactive-sessions/interactive-sessions-actions';
import { setCollections } from './components/search-catalog/search-catalog-slice';
import {
  handleDMJobsUpdate,
  loadCollections,
  loadQueries as loadSearchCatalogQueries,
} from './components/search-catalog/search-catalog-actions';
import {
  rD_setComputeServices,
  rD_setStorageAreas,
  rD_updateComputeServiceStatus,
  rD_updateRucioLocations,
} from './app/data-slice';
import {
  fnD_buildRucioLocations,
  fnD_getAvailableLocations,
  fnD_updateDataLocations,
} from './app/data-actions';
import {
  rG_setLoggedIn,
  rG_setSessionValid,
  rG_setSiteCapabilitiesTokenObtained,
  rG_setDataManagementTokenObtained,
  rG_setGatewayBackendToken,
  rG_setProject,
  rG_setSelectedCanfar,
  rG_setSite,
  rG_setUsername,
  rG_setPreferredUsername,
  rG_setDarkMode,
  rG_setTopMenuPosition,
} from './app/global-slice';
import {
  rS_setThemeSetting,
  rS_setLanguageSetting,
  rS_setIconSizeSetting,
  rS_setFontSizeSetting,
  rS_setDefaultProjectSetting,
  rS_setDefaultSiteUUIDSetting,
  rS_setPopupNotificationsEnabledSetting,
  rS_setPopupLogsEnabledSetting,
  rS_setSettingsLoaded,
} from './app/settings-slice';
import {
  addNotification,
  clearGatewayLogs,
  prependGatewayLogs,
  setFontSize,
  setIntermediateTool,
  setJupyterBaseURL,
  setJupyterRender,
  setJupyterURL,
  setLanguageDropdownMenuDisplayed,
  setLanguageIndex,
  setNotifications,
  setProjects,
  setSelectedTool,
  setShowNewSessionModal,
  setShowPreferencesModal,
  setTheme,
  setUserDropdownMenuDisplayed,
  setXsrfToken,
} from './app-slice';
import { updateComputeSearchServiceStatus } from './components/search-compute/search-compute-actions';
import {
  checkUserSession,
  fetchJobs,
  handleEvent,
  initializeAppData,
  loadBackendLogs,
  persistLanguageSelection,
} from './app-actions';
import {
  setProjectItems,
  setSelectedDivs as setSelectedDivsInProject,
  setSelectedItems as setSelectedItemsInProject,
} from './components/project/project-slice';
import {
  appendJobUpdates,
  setDataManagementJobs,
  updateDataManagementJobs,
} from './components/data-management/file-browser-slice';
import { cancelQuery as cancelFileBrowserQuery } from './components/data-management/file-browser-actions';
import { ctx_keyDownHandler } from './components/menus/context-menu-actions';
import {
  CONTEXT_SYSTEMS,
  ctx_setContextVisible,
} from './components/menus/context-menu-slice';

//	--------------------------------------------------------------------------
//
//	C O N S T A N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	M A I N   A P P L I C A T I O N
//
//	--------------------------------------------------------------------------

function App() {
  //
  // multi-language support
  const { i18n, t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // app state variables.
  const sShowNewSessionModal: {
    show: boolean;
    site_uuid?: string;
    service_uuid?: string;
    service_type?: string;
  } = useAppSelector((state) => state.app.showNewSessionModal);
  const sShowPreferencesModal: boolean = useAppSelector(
    (state) => state.app.showPreferencesModal,
  );

  //
  // DATA
  // -------------------------

  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  // hold the list of CANFAR services from the site-capabilities catalog.
  const sD_canfarServices: CanfarServiceType[] | undefined = useAppSelector(
    (state) => state.data.canfarServices,
  );

  // hold the list of compute services from the site-capabilities catalog.
  const sD_computeServices: ComputeServiceType[] = useAppSelector(
    (state) => state.data.computeServices,
  );

  // the locations in Rucio for monitored data items.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  );

  //
  // GLOBAL
  // -------------------------

  const sG_LoggedIn: boolean | undefined = useAppSelector(
    (state) => state.global.loggedIn,
  );
  const sG_SessionValid: boolean | undefined = useAppSelector(
    (state) => state.global.sessionValid,
  );
  const sG_LoggedInRefreshTrigger: number | undefined = useAppSelector(
    (state) => state.global.loggedInRefreshTrigger,
  );
  const sG_SiteCapabilitiesTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.siteCapabilitiesTokenObtained,
  );
  const sG_DataManagementTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.dataManagementTokenObtained,
  );
  const sG_GatewayBackendToken: string | undefined = useAppSelector(
    (state) => state.global.gatewayBackendToken,
  );
  const sG_Site: Site | undefined = useAppSelector((state) => state.global.site);
  const sG_Project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_Username: string | undefined = useAppSelector(
    (state) => state.global.username,
  );
  const sG_PreferredUsername: string | undefined = useAppSelector(
    (state) => state.global.preferredUsername,
  );
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_tabPosition: number = useAppSelector(
    (state) => state.global.topMenuPosition.leftLower,
  );

  const sLanguage: string | null = useAppSelector((state) => state.app.language);

  //
  // SETTINGS
  // --------------------------

  const sS_DefaultProject: number | undefined = useAppSelector(
    (state) => state.settings.defaultProject,
  );
  const sS_defaultSiteUUID: string | undefined = useAppSelector(
    (state) => state.settings.defaultSiteUUID,
  );
  const sS_settingsLoaded: boolean = useAppSelector(
    (state) => state.settings.settingsLoaded,
  );

  //
  // INTERACTIVE SESSIONS
  // --------------------------

  const sSessionsLoaded: boolean = useAppSelector(
    (state) => state.interactiveSessions.sessionsLoaded,
  );

  //
  // HOME
  // --------------------------

  const sProjectItems: DataItemType[] = useAppSelector(
    (state) => state.project.projectItems,
  );

  //
  // DATA MANAGEMENT
  // --------------------------
  const sDataManagementJobs = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );
  const sJobsLoading = useAppSelector((state) => state.fileBrowser.jobsLoading);
  const sJobUpdates = useAppSelector((state) => state.fileBrowser.jobUpdates);

  //	--------------------------------------------------------------------------
  //
  //	C O N S T A N T S
  //
  //	--------------------------------------------------------------------------

  const MODAL_STYLE = {
    color: sG_DarkMode ? '#fff' : '#000',
    borderRadius: '0px',
    margin: '0px',
  };

  const navigate = useNavigate();
  const location = useLocation();

  // define the toolset available along the top of the page.
  const TOOLS: ToolDescription[] = [
    {
      text: 'Home',
      toolType: ToolType.HOME,
      link: toolTypeToPath[ToolType.HOME],
      subItems: [],
    },
    {
      text: 'Project',
      toolType: ToolType.PROJECT,
      link: toolTypeToPath[ToolType.PROJECT],
      subItems: [],
    },
    {
      text: 'Data',
      toolType: ToolType.DATA,
      link: '#',
      subItems: [
        {
          text: 'Search catalogue',
          toolType: ToolType.SEARCH_CATALOG,
          link: toolTypeToPath[ToolType.SEARCH_CATALOG],
          subItems: [],
        },
        /*{
          text: 'Search catalogue (CAOM)',
          toolType: ToolType.SEARCH_CATALOG_CAOM,
          link: toolTypeToPath[ToolType.SEARCH_CATALOG_CAOM],
          subItems: [],
        },*/
        {
          text: 'File browser',
          toolType: ToolType.FILE_BROWSER,
          link: toolTypeToPath[ToolType.FILE_BROWSER],
          subItems: [],
          needDataManagement: true,
          needLoggedIn: true,
        },
        {
          text: 'Data-transfer jobs',
          toolType: ToolType.DATA_TRANSFER_JOBS,
          link: toolTypeToPath[ToolType.DATA_TRANSFER_JOBS],
          subItems: [],
          needDataManagement: true,
          needLoggedIn: true,
        },
      ],
    },
    {
      text: 'Compute',
      toolType: ToolType.COMPUTE,
      link: '#',
      subItems: [
        {
          text: 'Search compute resources',
          toolType: ToolType.SEARCH_COMPUTE,
          link: toolTypeToPath[ToolType.SEARCH_COMPUTE],
          subItems: [],
          needSiteCapabilities: true,
          needLoggedIn: true,
        },
        {
          text: 'Compute jobs',
          toolType: ToolType.COMPUTE_JOBS,
          link: toolTypeToPath[ToolType.COMPUTE_JOBS],
          subItems: [],
          needLoggedIn: true,
        },
        {
          text: 'Search software',
          toolType: ToolType.SEARCH_SOFTWARE,
          link: toolTypeToPath[ToolType.SEARCH_SOFTWARE],
          subItems: [],
          needLoggedIn: true,
        },
      ],
    },
    {
      text: 'Interactive sessions',
      toolType: ToolType.INTERACTIVE_SESSIONS,
      link: '#',
      subItems: [
        {
          text: 'Notebook',
          toolType: ToolType.NOTEBOOK,
          link: toolTypeToPath[ToolType.NOTEBOOK],
          subItems: [],
        },
        {
          text: 'Visualise data',
          toolType: ToolType.VISUALISE_DATA,
          link: toolTypeToPath[ToolType.VISUALISE_DATA],
          subItems: [],
        },
        {
          text: 'Virtual desktops',
          toolType: ToolType.VIRTUAL_DESKTOPS,
          link: toolTypeToPath[ToolType.VIRTUAL_DESKTOPS],
          subItems: [],
        },
      ],
    },
    /*,
  {
  name: 'btnUserManagement',
  icon: undefined,
  text: 'User management',
  toolType: ToolType.USER_MANAGEMENT,
  needLoggedIn: true
  },
  {
  name: 'btnWorkflow',
  icon: {workflowIcon},
  text: 'Workflow',
  toolType: ToolType.WORKFLOW
  },
  {
  name: 'btnTerminal',
  icon: {terminalIcon},
  text: 'Terminal',
  toolType: ToolType.TERMINAL
  },*/
  ];

  const ALL_ROUTES: ToolDescription[] = [
    ...TOOLS,
    ...[
      {
        text: 'View logs',
        toolType: ToolType.LOGS,
        link: toolTypeToPath[ToolType.LOGS],
        subItems: [],
      },
      {
        text: 'View notifications',
        toolType: ToolType.VIEW_NOTIFICATIONS,
        link: toolTypeToPath[ToolType.VIEW_NOTIFICATIONS],
        subItems: [],
      },
      {
        text: 'OIDC tokens',
        toolType: ToolType.OIDC_TOKENS,
        link: toolTypeToPath[ToolType.OIDC_TOKENS],
        subItems: [],
      },
    ],
  ];

  //	--------------------------------------------------------------------------
  //
  //	C A L L B A C K   F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	-------------------------------------------------
  //
  //	A notification has arrived.
  //
  //	-------------------------------------------------

  function notificationCreatedCallback(event: MessageEvent) {
    const payload: any = event.data ? JSON.parse(event.data) : null;

    if (!payload) return;

    var newNotification: NotificationType = {
      notificationID: payload.notification_id,
      notificationType: payload.notification_type,
      notificationText: payload.notification_text,
      readFlag: payload.read_flag,
      additionalInfo: payload.additional_info,
      createdAt: payload.created_at,
      displayPopup: true,
      sseExtras: payload.sse_extras,
    };

    dispatch(addNotification(newNotification));

    if (newNotification.notificationType === 'DMR') {
      dispatch(appendJobUpdates(newNotification));
    }
  }

  //	-------------------------------------------------
  //
  //	The status of a compute service has changed.
  //
  //	-------------------------------------------------

  // FIXME: Move these 3 functions to functions.tsx
  function computeAvailabilityCallback(event: MessageEvent) {
    //
    // receive payload.
    const payload: any = event.data ? JSON.parse(event.data) : {};
    let updates: number = 0;
    if ('service_updates' in payload) {
      updates = payload.service_updates.length;
    }

    // raise an event for the service updates supplied in the SSE, in order to notify
    // components that services has changed their status.
    var eventParams: ServiceStatusParams[] = [];
    for (let i: number = 0; i < updates; i++) {
      if (payload.service_updates[i].id) {
        const serviceStatus: ServiceStatusParams = {
          id: payload.service_updates[i].id,
          activeStatus:
            'active_status' in payload.service_updates[i]
              ? payload.service_updates[i].active_status
              : false,
          healthStatus:
            'health_status' in payload.service_updates[i]
              ? payload.service_updates[i].health_status
              : 'unknown',
          topBanner:
            'top_banner' in payload.service_updates[i]
              ? payload.service_updates[i].top_banner
              : 'unknown',
          bottomBannerType:
            'bottom_banner_type' in payload.service_updates[i]
              ? payload.service_updates[i].bottom_banner_type
              : 'text',
          bottomBanner:
            'bottom_banner' in payload.service_updates[i]
              ? payload.service_updates[i].bottom_banner
              : 'unknown',
        };
        eventParams.push(serviceStatus);
      }
    }
    if (eventParams.length > 0) {
      const computeAvailabilityEvent: Event = {
        eventTarget: EventTarget.GLOBAL,
        eventType: EventType.COMPUTE_SERVICE_STATUS_CHANGED,
        parameters: eventParams,
      };
      dispatch(handleEvent({ event: computeAvailabilityEvent }));
    }
    //
  } // computeAvailabilityCallback

  function dataCollectionsCallback(event: MessageEvent) {
    console.log('SSE event received: Data collection change', event.data);
    try {
      const eventData = JSON.parse(event.data);
      const action = eventData.action;

      let eventType: EventType;
      let eventTarget: EventTarget = EventTarget.GLOBAL;
      let parameters: any = undefined;

      // Map SSE action to frontend event type and transform payload
      switch (action) {
        case 'DATA_COLLECTION_ADDED':
          eventType = EventType.DATA_COLLECTION_ADDED;
          // Transform to AddCollectionEvent format
          parameters = {
            collectionName: eventData.collection_name,
            projectName: eventData.project_name,
            itemsAdded: eventData.items_added || 0,
            messageTarget: EventTarget.GLOBAL,
          } as AddCollectionEvent;
          break;
        case 'DATA_COLLECTION_DELETED':
          eventType = EventType.DATA_COLLECTION_DELETED;
          // Expected payload: just the project name as string
          parameters = eventData.project_name;
          break;
        case 'ITEMS_ADDED_TO_DATA_COLLECTION':
          eventType = EventType.ITEMS_ADDED_TO_DATA_COLLECTION;
          // Transform to AddCollectionEvent format
          parameters = {
            collectionName: eventData.collection_name,
            projectName: eventData.project_name,
            itemsAdded: eventData.items_added || 0,
            messageTarget: EventTarget.GLOBAL,
          } as AddCollectionEvent;
          break;
        case 'ITEMS_REMOVED_FROM_DATA_COLLECTION':
          eventType = EventType.ITEMS_REMOVED_FROM_DATA_COLLECTION;
          // Expected payload: no payload (undefined)
          parameters = undefined;
          break;
        default:
          console.warn('Unknown data collection action:', action);
          return;
      }

      // Create and raise the event with transformed payload
      const dataCollectionEvent: Event = {
        eventTarget: eventTarget,
        eventType: eventType,
        parameters: parameters,
      };
      dispatch(handleEvent({ event: dataCollectionEvent }));
    } catch (error) {
      console.error('Failed to parse data collection SSE event:', error);
    }
  } // dataCollectionsCallback

  function apiStatusCallback(event: MessageEvent) {
    const apiStatusItems: APIStatusItem[] = JSON.parse(event.data);
    const parameters: APIStatusParams = { apiStatuses: apiStatusItems };

    const APIStatusEvent: Event = {
      eventTarget: EventTarget.HOME,
      eventType: EventType.API_STATUS_UPDATE,
      parameters: parameters,
    };

    dispatch(handleEvent({ event: APIStatusEvent }));
  }

  function computeJobsCallback(event: MessageEvent) {
    console.log('SSE: compute job update received', event.data);
    try {
      const eventData = JSON.parse(event.data);
      const action = eventData.action;

      let eventType: EventType;
      let eventTarget: EventTarget = EventTarget.COMPUTE_JOBS;
      let parameters: any = eventData.job;

      // Map SSE action to frontend event type
      switch (action) {
        case 'JOB_ADDED':
        case 'JOB_SUBMITTED':
          eventType = EventType.COMPUTE_JOB_ADDED;
          break;
        case 'JOB_UPDATED':
        case 'JOB_STATUS_CHANGED':
          eventType = EventType.COMPUTE_JOB_UPDATED;
          break;
        case 'JOB_DELETED':
        case 'JOB_COMPLETED':
          eventType = EventType.COMPUTE_JOB_DELETED;
          parameters = eventData.job_id;
          break;
        default:
          console.warn('Unknown compute job action:', action);
          return;
      }

      // Create and raise the event
      const computeJobEvent: Event = {
        eventTarget: eventTarget,
        eventType: eventType,
        parameters: parameters,
      };
      dispatch(handleEvent({ event: computeJobEvent }));
    } catch (error) {
      console.error('Failed to parse compute job SSE event:', error);
    }
  } // computeJobsCallback

  function dataLocationCallback(event: MessageEvent) {
    //
    const dataLocationItem: {
      namespace: string;
      filename: string;
      locations: {
        identifier: string;
        associated_storage_area_id: string;
        replicas: string[];
      }[];
    } = JSON.parse(event.data);

    // update the locations in the Rucio array.
    fnD_updateDataLocations({ dataLocationItem: dataLocationItem });
    //
  } // dataLocationCallback

  function backendLogsCallback(event: MessageEvent) {
    const payload: any = event.data ? JSON.parse(event.data) : null;

    if (!payload) return;

    const newLog: GatewayLogType = {
      user_id: payload.user_id,
      log_id: payload.log_id,
      level: payload.level,
      message: payload.message,
      component: payload.component,
      created_at: payload.created_at,
      metadata: payload.metadata,
    };

    dispatch(prependGatewayLogs(newLog));
  }

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // general:
  const sSelectedTool: ToolType = useAppSelector((state) => state.app.selectedTool);
  const sIntermediateTool: ToolType = useAppSelector(
    (state) => state.app.intermediateTool,
  );

  // language:
  const sLanguageDropdownMenuDisplayed: boolean = useAppSelector(
    (state) => state.app.languageDropdownMenuDisplayed,
  );

  const sFontSize: number = useAppSelector((state) => state.app.fontSize);
  const sTheme: string = useAppSelector((state) => state.app.theme);
  const sUserDropdownMenuDisplayed: boolean = useAppSelector(
    (state) => state.app.userDropdownMenuDisplayed,
  );

  // notebook tool:
  const sJupyterBaseURL = useAppSelector((state) => state.app.jupyterBaseURL);
  const sJupyterURL = useAppSelector((state) => state.app.jupyterURL);
  const xsrfToken = useAppSelector((state) => state.app.xsrfToken);
  const sLoadingToken = useAppSelector((state) => state.app.loadingToken);
  const sNotifications = useAppSelector((state) => state.app.notifications);
  const sLoadingSites = useAppSelector((state) => state.app.loadingSites);
  const sUpdateServiceStatus = useAppSelector((state) => state.app.updateServiceStatus);
  const sGatewayLogs = useAppSelector((state) => state.app.gatewayLogs);
  const sProjects = useAppSelector((state) => state.app.projects);
  const sLoadingProjects = useAppSelector((state) => state.app.loadingProjects);

  const sG_PopupLogsEnabled: boolean | undefined = useAppSelector(
    (state) => state.settings.popupLogsEnabled,
  );

  const refLogsFocusRow = useRef<string | undefined>(undefined);

  const { addSSECallback } = SSEHandler(sG_SessionValid);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Get the subitems for the selected tool.
  //
  //	------------------------------------------------------------

  function getToolSubItems() {
    //
    let subItems: ToolDescription[] = [];

    // check if any of the subitems are selected.
    for (let i: number = 0; i < TOOLS.length; i++) {
      for (let j: number = 0; j < TOOLS[i].subItems.length; j++) {
        //
        if (sSelectedTool === TOOLS[i].subItems[j].toolType) {
          subItems = TOOLS[i].subItems;
        }
        //
      }
    }

    // check if this tool is selected.
    for (let i: number = 0; i < TOOLS.length; i++) {
      if (sIntermediateTool === TOOLS[i].toolType) {
        subItems = TOOLS[i].subItems;
      }
    }

    return subItems;
    //
  } // getToolSubItems

  //	-------------------------------------------------
  //
  //	launch a Jupyter notebook when the user selects one from
  //	the compute-resource search
  //
  //	-------------------------------------------------
  const fetchXsrfToken = async (url: string) => {
    console.log('fetchXsrfToken fires');
    try {
      const response = await fetch(
        url + (url.slice(-1) === '/' ? '' : '/') + 'hub/login',
        {
          credentials: 'include',
        },
      );

      if (response.ok) {
        const htmlText = await response.text();
        //console.log("HTML " + htmlText);
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const xsrfInput = doc.querySelector('input[name="_xsrf"]') as HTMLInputElement;

        console.log('XSRF ' + xsrfInput.value);
        if (xsrfInput) {
          console.log('setting XSRF token');
          dispatch(setXsrfToken(xsrfInput.value));
        }
      }
    } catch (error) {
      console.error('Failed to fetch XSRF token:', error);
    }
  };

  const loginUserJupyterHub = async (url: string) => {
    try {
      const formData = new FormData();
      console.log('loginUserJupyterHub fires');
      if (sG_GatewayBackendToken !== undefined) {
        formData.append('token', sG_GatewayBackendToken || '');
        formData.append('_xsrf', xsrfToken || '');

        // clear the XSRF token.
        dispatch(setXsrfToken(null));

        const response = await fetch(
          url + (url.slice(-1) === '/' ? '' : '/') + 'hub/login',
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(formData as any).toString(),
          },
        );

        if (response.ok) {
          dispatch(setJupyterURL(url + (url.slice(-1) === '/' ? '' : '/') + 'hub/spawn'));
          dispatch(setJupyterRender(Date.now()));
          dispatch(setSelectedTool(ToolType.NOTEBOOK));
          navigate(toolTypeToPath[ToolType.NOTEBOOK]);
        } else {
          console.error('Login Failed :(', response.statusText);
        }
      }
    } catch (error) {
      console.error('Error occurred while Login :(', error);
    }
  };

  const launchNotebook = async (args: { url: string }) => {
    try {
      // update the url and the render count (we need this to force a re-render when the user clicks 'launch' again).
      if (args.url !== undefined) {
        //TODO: Firstly we need clear any tokens stored for JHub. Assuming the user launchd the notebook before
        console.log('launch notebook: ' + args.url);
        console.log('preferred username: ' + sG_PreferredUsername);
        if (sG_PreferredUsername) {
          //const jupyter_username = JSON.parse(session_username);
          const response = await fetch(
            args.url + (args.url.slice(-1) === '/' ? '' : '/') + 'hub/logout',
            {
              credentials: 'include',
            },
          );
          console.log('launchNotebook: logout response received');
          console.log(args.url);
          console.log(response);
          if (response.ok) {
            // In order to re-SSO the user to JupyterHubs, we need to extract the _xsrf token
            dispatch(setJupyterBaseURL(args.url));
            console.log('launchNotebook: fetching XSRF token');
            await fetchXsrfToken(args.url);
          } else {
            throw new Error('Logout from JupyterHub failed.');
          }
        }
      }
    } catch (error) {
      console.error('Error occurred while launching notebook :(', error);
    }
  }; // launchNotebook

  useEffect(() => {
    if (xsrfToken && sG_GatewayBackendToken !== undefined) {
      loginUserJupyterHub(sJupyterBaseURL);
    }
  }, [xsrfToken, sG_GatewayBackendToken]);

  //	-------------------------------------------------
  //
  //	load and apply the user settings
  //
  //	-------------------------------------------------

  async function loadUserSettings() {
    //
    try {
      // call the /get_preferences endpoint here.
      const apiResult = await fetch(APIPrefix() + '/v1/get_preferences', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        //
        const returnedJson = await apiResult.json();
        console.log('loadUserSettings: returnedJson:', returnedJson);

        const darkMode: boolean =
          returnedJson.theme === Theme.AUTO
            ? getBrowserPreferredDarkMode()
            : returnedJson.theme === Theme.DARK;

        // set theme in cookie.
        setTheme(darkMode ? 'dark' : 'light');

        // set theme in Redux state.
        dispatch(rS_setThemeSetting(returnedJson.theme));

        // set theme in app state.
        dispatch(rG_setDarkMode(darkMode));

        if (returnedJson.language !== '') {
          //
          // change the language in the cookie.
          dispatch(persistLanguageSelection(returnedJson.language));

          // update the language in the Redux state.
          dispatch(rS_setLanguageSetting(returnedJson.language));
          //
        }

        if (
          returnedJson.icon_size.toLowerCase() === 'list' ||
          returnedJson.icon_size.toLowerCase() === 'medium' ||
          returnedJson.icon_size.toLowerCase() === 'large'
        ) {
          dispatch(rS_setIconSizeSetting(returnedJson.icon_size.toLowerCase()));
        } else {
          dispatch(rS_setIconSizeSetting('medium'));
        }

        if (Number(returnedJson.font_size)) {
          dispatch(rS_setFontSizeSetting(Number(returnedJson.font_size)));
          setFontSize(Number(returnedJson.font_size));
        }

        if (Number(returnedJson.default_project)) {
          dispatch(rS_setDefaultProjectSetting(Number(returnedJson.default_project)));
        }

        if (
          returnedJson.default_site_uuid !== undefined &&
          returnedJson.default_site_uuid !== null &&
          returnedJson.default_site_uuid !== ''
        ) {
          dispatch(rS_setDefaultSiteUUIDSetting(returnedJson.default_site_uuid));
        }

        if (
          returnedJson.popup_notifications_enabled !== undefined &&
          returnedJson.popup_notifications_enabled != null
        ) {
          dispatch(
            rS_setPopupNotificationsEnabledSetting(
              returnedJson.popup_notifications_enabled,
            ),
          );
        }

        if (
          returnedJson.popup_errors_enabled !== undefined &&
          returnedJson.popup_errors_enabled != null
        ) {
          dispatch(rS_setPopupLogsEnabledSetting(returnedJson.popup_errors_enabled));
        }
        //
      }

      // HTTP 401 means the session has expired.
      if (apiResult.status === 401) {
        console.log('loadUserSettings 401.');
      }
    } catch (e) {}
    dispatch(rS_setSettingsLoaded(true));
    //
  } // loadUserSettings

  const sG_PreviousLogsCount = useRef(sGatewayLogs.length);

  // TODO simplify logs popup code
  useEffect(() => {
    if (sG_PopupLogsEnabled) {
      const newLogsCount: number = sG_PreviousLogsCount.current
        ? sGatewayLogs.length - sG_PreviousLogsCount.current
        : 0;
      //    setPreviousLogsCount(sGatewayLogs.length);
      console.table(sGatewayLogs);
      console.groupEnd();

      sG_PreviousLogsCount.current = sGatewayLogs.length;
      let messages: string[] = [];
      for (var i: number = 0; i < newLogsCount && messages.length < 5; i++) {
        messages.push(sGatewayLogs[0].created_at + ' ' + sGatewayLogs[0].message);
      }
      if (messages.length) {
        toast.error(
          <CustomLogsToast messages={messages} path={toolTypeToPath[ToolType.LOGS]} />,
          {
            className: 'log-toast',
            position: 'bottom-left',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: sG_DarkMode === true ? 'dark' : 'light',
          },
        );
      }
    }
  }, [sGatewayLogs]);

  //	------------------------------------------------------------
  //
  //	Call the API to log out by removing the session cookies
  //
  //	------------------------------------------------------------

  async function logout() {
    // remove cookies, and update state variables.
    navigate(toolTypeToPath[ToolType.HOME]);
    dispatch(setJupyterURL(''));
    setDataManagementJobs([]);
    dispatch(setNotifications([]));
    dispatch(rD_setStorageAreas([]));
    dispatch(rD_setComputeServices([]));
    setProjects([]);
    dispatch(clearGatewayLogs());
    sessionStorage.clear();
    dispatch(rG_setLoggedIn(false));
    dispatch(rG_setSessionValid(false));
    dispatch(rG_setUsername(undefined));
    dispatch(rG_setPreferredUsername(undefined));
    dispatch(rG_setSiteCapabilitiesTokenObtained(false));
    dispatch(rG_setDataManagementTokenObtained(false));
    dispatch(rG_setGatewayBackendToken(undefined));
    dispatch(rS_setSettingsLoaded(false));

    // return to the homepage if we're on a restricted tab.
    navigate(toolTypeToPath[ToolType.HOME], { replace: true });

    var urlCommand: string = APIPrefix() + '/v1/logout';

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
    } catch (e) {}
  } // logout

  //	-------------------------------------------------
  //
  //	handles button click events on the main form.
  //
  //	-------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    event.preventDefault();

    const button: HTMLButtonElement = event.currentTarget;

    // if the login button is clicked then display or hide the pop-up login box.
    if (button.name === 'btnLogin') {
      // connect to SKA IAM and OIDC.
      redirectToLoginURL(location.pathname + location.search);
    }

    // if the user account button is clicked then display or hide the dropdown menu.
    if (button.name === 'btnUserAccount' && sUserDropdownMenuDisplayed === false)
      dispatch(setUserDropdownMenuDisplayed(true));
    else dispatch(setUserDropdownMenuDisplayed(false));

    // logout button from the user control dropdown menu.
    if (button.name === 'btnLogout') {
      dispatch(setUserDropdownMenuDisplayed(false));

      // log out of the backend.
      logout();
    }

    // OIDC token button from the user control dropdown menu.
    if (button.name === 'btnOIDCToken') {
      dispatch(setSelectedTool(ToolType.OIDC_TOKENS));
      navigate(toolTypeToPath[ToolType.OIDC_TOKENS]);
    }

    // OIDC token button from the user control dropdown menu.
    if (button.name === 'btnProfile') {
      setSelectedTool(ToolType.USER_PROFILE);
      navigate(toolTypeToPath[ToolType.USER_PROFILE]);
    }

    // user preferences dialog
    if (button.name === 'btnPreferences') {
      dispatch(setShowPreferencesModal(true));
    }

    // language menu.
    if (button.name === 'lstLanguage' && sLanguageDropdownMenuDisplayed === false)
      dispatch(setLanguageDropdownMenuDisplayed(true));
    else dispatch(setLanguageDropdownMenuDisplayed(false));

    // view all notifications.
    if (button.name === 'viewNotifications') {
      dispatch(setSelectedTool(ToolType.VIEW_NOTIFICATIONS));
      navigate(toolTypeToPath[ToolType.VIEW_NOTIFICATIONS]);
    }

    // user guide.
    if (button.name === 'user-guide') {
      dispatch(setSelectedTool(ToolType.USER_GUIDE));
      navigate(toolTypeToPath[ToolType.USER_GUIDE]);
    }

    // update the current language.
    if (button.name.length > 12)
      if (button.name.slice(0, 12) === 'btnLanguage_') {
        // get the rest of the text.
        var language: string = button.name.slice(12 - button.name.length);

        // update the language index.
        var languageIndex: number | undefined = findLanguageIndex({ language: language });
        dispatch(setLanguageIndex(languageIndex ? languageIndex : undefined));

        // change the language.
        dispatch(persistLanguageSelection(language));
      }

    // if the user has clicked the logs button, then change the current tool.
    if (button.name === 'btnLogs') {
      dispatch(setSelectedTool(ToolType.LOGS));
      navigate(toolTypeToPath[ToolType.LOGS]);
    }

    if (button.id.startsWith('site_')) {
      const indexStr: string = button.id.substring('site_'.length);
      const index: number = Number(indexStr);

      const arrayIndex = sD_sites.findIndex((element) => element.index === index);
      if (arrayIndex > -1) {
        dispatch(rG_setSite(sD_sites[arrayIndex]));
      }

      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
      };
      dispatch(handleEvent({ event: newEvent }));
      return;
    }

    if (button.id.startsWith('project_')) {
      const indexStr: string = button.id.substring('project_'.length);
      const index: number = Number(indexStr);

      const arrayIndex = sProjects.findIndex((element) => element.id === index);
      if (arrayIndex > -1) {
        dispatch(
          rG_setProject({
            id: sProjects[arrayIndex].id,
            name: sProjects[arrayIndex].name,
          }),
        );

        const newEvent: Event = {
          eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
          eventType: EventType.INITIATE_DM_SEARCH,
        };
        dispatch(handleEvent({ event: newEvent }));
      }
    }
    //
  }; // onClickHandler

  //	-------------------------------------------------
  //
  //	handle a key press somewhere in the application.
  //
  //	-------------------------------------------------

  function onKeyDownHandler(evt: React.KeyboardEvent<HTMLElement>) {
    //
    // pass this event on to the context-menu event handler.
    const handled: boolean = ctx_keyDownHandler({ key: evt.key });

    // stop any default actions, such as triggering the focused component,
    // if the event has been handled by the context-menu handler.
    if (handled === true) {
      evt.preventDefault();
    }

    // if we're running queries, and escape is pressed, then
    // cancel the query.
    const state: any = store.getState();
    if (state.fileBrowser.loadingComponent === true && evt.key === 'Escape') {
      cancelFileBrowserQuery();
    }
    //
  } // onKeyDownHandler

  function registerSSECallbacks() {
    //
    console.log('Registering callbacks');

    addSSECallback('notification_created', notificationCreatedCallback);
    addSSECallback('compute_services', computeAvailabilityCallback);
    addSSECallback('logs', backendLogsCallback);
    addSSECallback('data_collections', dataCollectionsCallback);
    addSSECallback('api_status', apiStatusCallback);
    addSSECallback('compute_jobs', computeJobsCallback);
    addSSECallback('data_location', dataLocationCallback);
    console.log('Registered callbacks');
    //
  } // registerSSECallbacks

  //	-------------------------------------------------
  //
  //	Check if we hold a supplied compute service ID
  //	in the state array, and if so we should update
  //	the status.
  //
  //	-------------------------------------------------

  function updateComputeStatus(args: { serviceStatus: ServiceStatusParams }) {
    //
    console.log('SSE: updateComputeService event handled by raiseEvent in App.tsx');

    dispatch(
      rD_updateComputeServiceStatus({
        id: args.serviceStatus.id,
        activeStatus: args.serviceStatus.activeStatus,
        healthStatus: args.serviceStatus.healthStatus,
      }),
    );
    dispatch(updateComputeSearchServiceStatus(args.serviceStatus));
    //
  } // updateComputeStatus

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	things to do when the page loads.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //

    // define hook to register callback functions for Server Sent Events.
    console.log('setting up server-sent event handlers');
    registerSSECallbacks();

    // set the currently selected tool by reading the URL.
    let url: string = window.location.href;

    // extract the part after the last '/'/
    if (url.lastIndexOf('/') > -1) {
      //
      if (url.length === url.lastIndexOf('/') + 1) {
        url = '';
      } else {
        url = url.slice(-(url.length - url.lastIndexOf('/') - 1));
      }

      for (const key in toolTypeToPath) {
        const idx: ToolType = Number(key);
        if (toolTypeToPath[idx] === '/' + url) {
          dispatch(setSelectedTool(idx));
        }
      }
      if (url === '') {
        dispatch(setSelectedTool(ToolType.HOME));
        navigate(toolTypeToPath[ToolType.HOME]);
      }

      // update the theme based upon the cookie value.
      dispatch(
        rG_setDarkMode(
          sTheme === 'auto' ? getBrowserPreferredDarkMode() : sTheme === 'dark',
        ),
      );

      // load the data-product types for the search-catalog page.
      dispatch(loadDataProductTypes());

      // display the root page on the DM tab.
      // run the data-management search.
      const params: InitiateDMSearchParams = {
        pageDisplayed: FileBrowserPage.FileBrowser,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
      //
    }
    //
  }, []);

  //  ------------------------------------------------------------
  //
  //  set HTTP Content-Language representation header
  //  based on user selected language
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    document.documentElement.lang = sLanguage ? sLanguage : '';
  }, [sLanguage]);

  //	------------------------------------------------------------
  //
  //	hide any open context menus when the selected tool
  //	changes.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    Object.keys(CONTEXT_SYSTEMS).forEach((key, index) => {
      //
      dispatch(
        ctx_setContextVisible({
          contextSystem: index,
          visible: false,
        }),
      );
      //
    });

    // navigate to the selected tool.
    navigate(toolTypeToPath[sSelectedTool]);
    //
  }, [sSelectedTool]);

  //	------------------------------------------------------------
  //
  //	select a new instance of CANFAR when the selected site
  //	has changed.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log(
      '[sG_Site, sG_CanfarServices] hook fires (App)',
      sD_canfarServices?.length,
      sG_Site,
    );
    if (
      sD_canfarServices !== undefined &&
      sD_canfarServices.length > 0 &&
      sG_Site !== undefined
    ) {
      //
      for (let i: number = 0; i < sD_canfarServices.length; i++) {
        if (sD_canfarServices[i].site.site_uuid === sG_Site.site_uuid) {
          //
          dispatch(rG_setSelectedCanfar(sD_canfarServices[i]));

          // refresh the file browser.
          const params: InitiateDMSearchParams = {
            canfarService: sD_canfarServices[i],
          };
          const newEvent: Event = {
            eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
            eventType: EventType.INITIATE_DM_SEARCH,
            parameters: params,
          };
          dispatch(handleEvent({ event: newEvent }));

          return;
          //
        }
      }
      dispatch(rG_setSelectedCanfar(undefined));
    } else {
      dispatch(rG_setSelectedCanfar(undefined));
    }
    //
  }, [sG_Site, sD_canfarServices]);

  //	------------------------------------------------------------
  //
  //	retrieve a fresh list of SRCNet and CANFAR compute services
  //	once the list of compute services has been populated.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    if (
      sD_computeServices.length > 0 &&
      sD_sites.length > 0 &&
      sD_canfarServices !== undefined &&
      sSessionsLoaded === false
    ) {
      dispatch(setSessionsLoaded(true));
      dispatch(refreshSRCNetServices());
      dispatch(refreshCanfarServices({}));
    }
    //
  }, [sD_computeServices, sD_sites, sD_canfarServices, sSessionsLoaded, dispatch]);

  //	-------------------------------------------------
  //
  //	define a hook that loads a list of storage areas
  //	and local compute services when logged in.
  //
  //	-------------------------------------------------

  useEffect(() => {
    if (sG_LoggedIn === true) {
      // load user settings.
      loadUserSettings();

      const notificationsEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_NOTIFICATIONS,
      };
      dispatch(handleEvent({ event: notificationsEvent }));

      // get the data management jobs.
      dispatch(fetchJobs());

      // get the list of compute jobs.
      const computeJobEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.GET_COMPUTE_JOBS,
      };
      dispatch(handleEvent({ event: computeJobEvent }));

      const logsEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.LOAD_BACKEND_LOGS,
      };
      dispatch(handleEvent({ event: logsEvent }));

      // load the list of projects.
      dispatch(initializeAppData());
    }
  }, [sG_LoggedIn, dispatch]);

  useEffect(() => {
    console.log('User session hook>>>>>>>>>>>>>>>>');
    dispatch(checkUserSession());
    const refreshInterval = setInterval(checkUserSession, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (sG_SessionValid === false && sG_LoggedIn === true) {
      console.log('User session expired>>>>>>>>>>>>>>>>');
      toast(
        <SessionToast
          onLogin={() => {
            void redirectToLoginURL(location.pathname + location.search);
          }}
        />,
      );
      // Log the user out
      logout();
    }
  }, [sG_SessionValid]);

  //	------------------------------------------------------------
  //
  //	Run the initial search once we have obtained a site
  //	capabilities token.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[props.siteCapabilitiesTokenObtained] hook fires [search-compute]');

    // run the search-compute search.
    if (sG_SiteCapabilitiesTokenObtained === true) {
      const params: InitiateComputeSearchParams = {};
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_COMPUTE_TABLE,
        eventType: EventType.INITIATE_COMPUTE_SEARCH,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  }, [sG_SiteCapabilitiesTokenObtained, dispatch]);

  //	------------------------------------------------------------
  //
  //	updates the current list of services from the state array
  //	of service-status updates.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('SSE: [sUpdateServiceStatus] hook fires [App]');

    // loop through the array of service updates, updating the state array.
    for (let i: number = 0; i < sUpdateServiceStatus.length; i++) {
      updateComputeStatus({ serviceStatus: sUpdateServiceStatus[i] });
    }
    //
  }, [sUpdateServiceStatus]);

  //	------------------------------------------------------------
  //
  //	selects a project from the project list if none is
  //	currently selected.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log(
      'SSE: [sProjects, sS_settingsLoaded] hook fires [App]',
      'sProjects.length:',
      sProjects.length,
      'sG_Project:',
      sG_Project,
      'sS_DefaultProject:',
      sS_DefaultProject,
      'sS_settingsLoaded:',
      sS_settingsLoaded,
    );

    if (sProjects.length > 0 && sG_Project === undefined && sS_settingsLoaded === true) {
      //
      // use the default project if one is specified.
      let index: number = -1;
      if (sS_DefaultProject !== undefined) {
        index = sProjects.findIndex((item) => item.id === sS_DefaultProject);
      }

      // if that fails, select the first project
      if (index === -1) {
        index = 0;
      }

      // update the state.
      dispatch(rG_setProject({ id: sProjects[index].id, name: sProjects[index].name }));
      //
    }
    //
  }, [sProjects, sS_settingsLoaded, sS_DefaultProject, dispatch, sG_Project]);

  //	------------------------------------------------------------
  //
  //	selects a site from the project list if none is
  //	currently selected.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log(
      'SSE: [sD_sites, sS_settingsLoaded] hook fires [App]',
      'sD_sites.length:',
      sD_sites.length,
      'sG_Site:',
      sG_Site,
      'sS_defaultSiteUUID:',
      sS_defaultSiteUUID,
      'sS_settingsLoaded:',
      sS_settingsLoaded,
    );

    if (sD_sites.length > 0 && sG_Site === undefined && sS_settingsLoaded === true) {
      //
      // use the default site if one is specified.
      let index: number = -1;
      if (sS_defaultSiteUUID !== undefined) {
        index = sD_sites.findIndex((item) => item.site_uuid === sS_defaultSiteUUID);
      }

      // if that fails, select the first site
      if (index === -1) {
        index = 0;
      }

      // update the state.
      dispatch(rG_setSite(sD_sites[index]));
      //
    }
    //
  }, [sD_sites, sS_settingsLoaded, sS_defaultSiteUUID, dispatch, sG_Site]);

  //	------------------------------------------------------------
  //
  //	selects a site from the project list if none is
  //	currently selected.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log(
      'SSE: [sG_sites, sS_settingsLoaded] hook fires [App]',
      'sD_sites.length:',
      sD_sites.length,
      'sG_Site:',
      sG_Site,
      'sS_defaultSiteUUID:',
      sS_defaultSiteUUID,
      'sS_settingsLoaded:',
      sS_settingsLoaded,
    );

    if (sD_sites.length > 0 && sG_Site === undefined && sS_settingsLoaded === true) {
      //
      // use the default site if one is specified.
      let index: number = -1;
      if (sS_defaultSiteUUID !== undefined) {
        index = sD_sites.findIndex((item) => item.site_uuid === sS_defaultSiteUUID);
      }

      // if that fails, select the first site
      if (index === -1) {
        index = 0;
      }

      // update the state.
      dispatch(rG_setSite(sD_sites[index]));
      //
    }
    //
  }, [sD_sites, sS_settingsLoaded]);

  //	------------------------------------------------------------
  //
  //	check for a change in the project, and update the list
  //	of existing data collections.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sG_Project] hook fires (App)', sG_Project);

    // either load the collections or clear the list.
    if (sG_Project !== undefined) {
      //
      // load collections, and initiate the search for project files.
      if (sG_Project.name !== '') {
        dispatch(loadCollections({ project: sG_Project.name }));
        const newEvent: Event = {
          eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
          eventType: EventType.INITIATE_PROJECT_FILES_SEARCH,
        };
        dispatch(handleEvent({ event: newEvent }));
      } else {
        dispatch(setCollections([]));
      }

      // load the existing queries that are displayed on the search-catalog new-search form.
      dispatch(loadSearchCatalogQueries({ project_id: sG_Project.id ?? -1 }));
      //
    } else {
      dispatch(setCollections([]));
      dispatch(setProjectItems([]));
    }

    // ensure nothing is selected.
    dispatch(setSelectedItemsInProject([]));
    dispatch(setSelectedDivsInProject([]));
    //
  }, [sG_Project]);

  //	------------------------------------------------------------
  //
  //	if the data-management jobs have refreshed, then for the
  //	selected items on the search-catalog page we need to:
  //
  //	- build a list of sites where that Rucio data is available.
  //	- build a list of sites where that Rucio data is currently
  //	  being staged to.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sDataManagementJobs] hook fires [App]');

    // update the available and moving locations of selected items
    // when the list of data-management jobs is updated.
    handleDMJobsUpdate({
      dataManagementJobs: sDataManagementJobs,
    });
    //
  }, [sDataManagementJobs]);

  //	------------------------------------------------------------
  //
  //	When the list of project items is changed then we need
  //	to find those items in Rucio.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sProjectItems hook fires [App]');

    fnD_buildRucioLocations({
      items: sProjectItems.map((item) => {
        return {
          namespace: item.namespace,
          filename: item.filename,
        };
      }),
      catalog: 'project-files',
      refresh: false,
    });
    //
  }, [sProjectItems]);

  //	------------------------------------------------------------
  //
  //	look up locations for any selected files where refresh is
  //	true.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    console.log('[sD_rucioLocations] hook fires [App]');

    // make a list of items that need to be refreshed.
    var refreshList: {
      namespace: string;
      filename: string;
    }[] = sD_rucioLocations
      .filter((item) => item.refresh === true)
      .map((item) => {
        return {
          namespace: item.namespace,
          filename: item.filename,
        };
      });

    // update the locations state so that all the refresh flags are false.
    if (refreshList.length > 0) {
      dispatch(
        rD_updateRucioLocations({
          items: refreshList.map((item) => {
            return {
              namespace: item.namespace,
              filename: item.filename,
              refresh: null,
              refreshingLocations: true,
            };
          }),
        }),
      );
    }

    // refresh the locations of these files. the responses will
    // come through asynchronously as server-sent events.
    dispatch(
      fnD_getAvailableLocations({
        items: refreshList,
        useAsync: true,
      }),
    );
    //
  }, [sD_rucioLocations]);

  useEffect(() => {
    // Monitors the job update buffer of notifications and processes them when jobs are
    // not being loaded. This avoids a race condition where updates may be received
    // for a job that hasn't yet been retrieved.
    if (!sJobsLoading && sJobUpdates && sJobUpdates.length > 0) {
      dispatch(updateDataManagementJobs(sJobUpdates));
    }
  }, [sJobsLoading, sJobUpdates, dispatch]);

  //	-------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	-------------------------------------------------

  return (
    <Suspense fallback="loading">
      <div className="mobile-warning" aria-live="polite">
        <div className="mw-card" role="dialog" aria-modal="true">
          <div className="mw-logo" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="56" height="56">
              <path
                d="M22 22 L42 42 M42 22 L22 42"
                stroke="yellow"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h1 className="mw-title">Window too small</h1>
          <p className="mw-text">
            SRCNet Science Gateway is best viewed on a tablet, laptop or desktop.
          </p>
        </div>
      </div>
      <Container
        fluid
        className="container-fluid"
        style={{ fontSize: sFontSize.toString() + 'pt' }}
        data-theme={sG_DarkMode ? 'dark' : 'light'}
        onKeyDown={onKeyDownHandler}
      >
        <Row className="page-header" style={{ padding: '8px 0px 0px 0px' }}>
          <Col
            sm={10}
            style={{
              alignItems: 'start',
              justifyContent: 'start',
              display: 'flex',
              flexDirection: 'row',
              padding: '4px 0px 4px 12px',
            }}
          >
            <div
              role="banner"
              style={{
                width: 'auto',
                height: 'auto',
                margin: '0px 10px 0px 0px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src={sG_DarkMode ? srcLogo : srcLightLogo}
                alt="SRCNet"
                width="168px"
              />
            </div>
            <div style={{ flex: '1 1' }} />
            <div
              role="navigation"
              aria-label="Navigate SRCNet"
              style={{ width: '960px', display: 'flex', flexDirection: 'column' }}
              onMouseLeave={(e: any) => {
                setIntermediateTool(ToolType.NONE);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: 'auto',
                  justifyContent: 'left',
                  marginBottom: '10px',
                }}
              >
                {TOOLS.map((item, index) => {
                  //
                  const isLoading: boolean =
                    sLoadingToken === true &&
                    (item.needSiteCapabilities === true ||
                      item.needDataManagement === true ||
                      item.needLoggedIn === true ||
                      item.needJupyterURL === true);

                  const isDisabled: boolean =
                    (item.needSiteCapabilities === true &&
                      !sG_SiteCapabilitiesTokenObtained) ||
                    (item.needDataManagement === true &&
                      !sG_DataManagementTokenObtained) ||
                    (item.needLoggedIn === true && sG_LoggedIn !== true) ||
                    (item.needJupyterURL === true && sJupyterURL === '');

                  // find if this tool, or this tool's parent tool, is currently displayed.
                  let isCurrentlyDisplayed: boolean = sSelectedTool === item.toolType;
                  for (let i: number = 0; i < TOOLS.length; i++) {
                    if (item.toolType === TOOLS[i].toolType) {
                      for (let j: number = 0; j < TOOLS[i].subItems.length; j++) {
                        if (sSelectedTool === TOOLS[i].subItems[j].toolType) {
                          isCurrentlyDisplayed = true;
                        }
                      }
                    }
                  }

                  let isSelected: boolean = false;
                  if (
                    sSelectedTool === item.toolType &&
                    sIntermediateTool === ToolType.NONE
                  ) {
                    isSelected = true;
                  }
                  if (sIntermediateTool === item.toolType) {
                    isSelected = true;
                  }
                  if (
                    isCurrentlyDisplayed === true &&
                    sIntermediateTool === ToolType.NONE
                  ) {
                    isSelected = true;
                  }

                  const path = toolTypeToPath[item.toolType];

                  if (path === '#') {
                    return (
                      <Tool
                        name={'tool' + index.toString()}
                        text={t(item.text)}
                        selected={isSelected}
                        secondarySelected={isCurrentlyDisplayed && isSelected === false}
                        disabled={isDisabled}
                        loading={isLoading}
                        onClick={(e: any) => {
                          if (isDisabled === false) {
                            if (isCurrentlyDisplayed === true) {
                              dispatch(setIntermediateTool(ToolType.NONE));
                            } else {
                              dispatch(setIntermediateTool(item.toolType));
                            }
                          }
                        }}
                        onMouseEnter={(e: any) => {
                          if (isDisabled === false) {
                            if (isCurrentlyDisplayed === true) {
                              dispatch(setIntermediateTool(ToolType.NONE));
                            } else {
                              dispatch(setIntermediateTool(item.toolType));
                            }
                          }
                        }}
                        largeText={true}
                        fixedWidth={true}
                        updatePosition={true}
                      />
                    );
                  } else {
                    return (
                      <NavLink
                        key={
                          index.toString() + t(item.text) + (sLoadingToken ? 'T' : 'F')
                        }
                        to={path}
                        className={({ isActive }) =>
                          `tool-link ${isActive && isSelected ? 'selected' : ''} ${isActive && isCurrentlyDisplayed && isSelected === false ? 'secondarySelected' : ''} ${isDisabled ? 'disabled' : ''}`
                        }
                        onClick={(e) => {
                          if (isDisabled) {
                            e.preventDefault();
                          } else {
                            dispatch(setSelectedTool(item.toolType));
                            dispatch(setIntermediateTool(ToolType.NONE));
                          }
                        }}
                        data-large-text="T"
                        end
                      >
                        {/* <span className='tool-icon'>{item.icon}</span> */}
                        <span>{item.text}</span>
                      </NavLink>
                    );
                  }
                })}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <div
                  ref={(element: any) => {
                    //
                    if (!element) {
                      return;
                    }

                    // update the size and offset of the sub-elements, so we can move them under the selected parent.
                    let update: {
                      leftUpper?: number;
                      widthUpper?: number;
                      widthLower?: number;
                      offsetLeftLower?: number;
                    } = { widthLower: element.getBoundingClientRect().width };
                    if (element.parentElement) {
                      update.offsetLeftLower = element.parentElement.offsetLeft;
                    }

                    dispatch(rG_setTopMenuPosition(update));
                    //
                  }}
                  style={{ position: 'relative', left: sG_tabPosition }}
                >
                  {getToolSubItems().map((item, index) => {
                    const isLoading =
                      sLoadingToken === true &&
                      (item.needSiteCapabilities ||
                        item.needDataManagement ||
                        item.needLoggedIn ||
                        item.needJupyterURL);

                    const isDisabled: boolean =
                      (item.needSiteCapabilities === true &&
                        sG_SiteCapabilitiesTokenObtained !== true) ||
                      (item.needDataManagement === true &&
                        sG_DataManagementTokenObtained !== true) ||
                      (item.needLoggedIn === true && sG_LoggedIn !== true) ||
                      (item.needJupyterURL === true && sJupyterURL === '');

                    const path = toolTypeToPath[item.toolType];

                    return (
                      <NavLink
                        key={
                          index.toString() + t(item.text) + (sLoadingToken ? 'T' : 'F')
                        }
                        to={path}
                        className={({ isActive }) =>
                          `tool-link ${isActive ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`
                        }
                        onClick={(e) => {
                          if (isDisabled) {
                            e.preventDefault();
                          } else {
                            dispatch(setSelectedTool(item.toolType));
                            dispatch(setIntermediateTool(ToolType.NONE));
                          }
                        }}
                        data-secondary="T"
                        end
                      >
                        {/* <span className='tool-icon'>{item.icon}</span> */}
                        <span>{item.text}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ flex: '1 1' }} />
          </Col>
          <Col sm={2} style={{ padding: '0px 5px 0px 0px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '10px 0px 10px 0px',
                alignItems: 'flex-end',
                justifyContent: 'center',
                height: '100%',
                gap: '0px',
              }}
            >
              <UserControl
                key={sG_Username + (sG_LoggedIn ? 'T' : 'F') + sG_LoggedInRefreshTrigger}
                buttonHandler={onClickHandler}
                userDropdownMenuDisplayed={sUserDropdownMenuDisplayed}
                setUserDropdownMenuDisplayed={setUserDropdownMenuDisplayed}
              />
            </div>
          </Col>
        </Row>
        <Row
          className="flex-grow-1"
          style={{ flex: '1 1 auto', minHeight: '0', overflow: 'hidden' }}
        >
          <Col
            className="d-flex flex-column min-h-0"
            style={{
              width: '150px',
              maxWidth: '150px',
              padding: '10px 0px 0px 5px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: '1 1', display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: '0 0 150px' }}>
                <ProjectPanel
                  sLoadingProjects={sLoadingProjects}
                  sGatewayLogs={sGatewayLogs}
                  onClickBtnHandler={onClickHandler}
                />
              </div>
            </div>
            <div
              className="project-button-container text-colour"
              style={{
                flex: '0 0 120px',
                width: '140px',
                borderRadius: '0px 0px 8px 8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
                paddingLeft: '6px',
                gap: '5px',
              }}
              data-pos=""
              data-minor="BUTTONS"
            >
              <div style={{ flex: '0 0 10px' }} />
              <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row' }}>
                <ToolButton
                  name="user-guide"
                  onClick={onClickHandler}
                  icon={magnifierWhiteIcon}
                  text={t('User guide')}
                  type={
                    sSelectedTool === ToolType.USER_GUIDE
                      ? sG_DarkMode
                        ? ToolButtonType.PRIMARY
                        : ToolButtonType.SECONDARY
                      : ToolButtonType.NONE
                  }
                  selected={sSelectedTool === ToolType.USER_GUIDE}
                  darkBackground={true}
                />
              </div>
              <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row' }}>
                <RoundButton
                  name="btnLogs"
                  icon={logsIcon}
                  onClickHandler={onClickHandler}
                  count={sGatewayLogs.length}
                  countOffset={true}
                  selected={sSelectedTool === ToolType.LOGS}
                  type={
                    sSelectedTool === ToolType.LOGS
                      ? sG_DarkMode
                        ? ToolButtonType.PRIMARY
                        : ToolButtonType.SECONDARY
                      : ToolButtonType.NONE
                  }
                  text="View logs"
                  textOnRight={true}
                  bigButton={true}
                  darkBackground={true}
                />
              </div>
            </div>
          </Col>
          <Col
            style={{
              overflowY: 'auto',
              padding: '10px 0px 0px 0px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <RouteChangeHandler tools={ALL_ROUTES} baseTitle={TITLE} />
            <Routes>
              {/* The home tab */}
              <Route
                path="/home"
                element={
                  <Home
                    dataManagementJobs={sDataManagementJobs}
                    jobsLoading={sJobsLoading}
                    logEntries={sGatewayLogs}
                    logsFocusRow={refLogsFocusRow}
                  />
                }
              />
              {/* The project tab */}
              <Route path="/project" element={<ProjectFiles />} />
              {/* The search catalog tab for data discovery */}
              <Route
                path={toolTypeToPath[ToolType.SEARCH_CATALOG]}
                element={<SearchCatalog dataManagementJobs={sDataManagementJobs} />}
              />
              {/* The search catalog (CAOM) tab for data discovery */}
              <Route
                path={toolTypeToPath[ToolType.SEARCH_CATALOG_CAOM]}
                element={<SearchCatalogCAOM dataManagementJobs={sDataManagementJobs} />}
              />
              {/* The search compute tab */}
              <Route
                path={toolTypeToPath[ToolType.SEARCH_COMPUTE]}
                element={
                  <SearchCompute tabVisible={sSelectedTool === ToolType.SEARCH_COMPUTE} />
                }
              />
              {/* The compute jobs tab */}
              <Route
                path={toolTypeToPath[ToolType.COMPUTE_JOBS]}
                element={<ComputeJobs />}
              />
              {/* The data-transfer jobs tab*/}
              <Route
                path={toolTypeToPath[ToolType.DATA_TRANSFER_JOBS]}
                element={
                  <JobStatus
                    dataManagementJobs={sDataManagementJobs}
                    jobsLoading={sJobsLoading}
                    logEntries={sGatewayLogs}
                    logsFocusRow={refLogsFocusRow}
                  />
                }
              />
              {/* The data management tab */}
              <Route
                path={toolTypeToPath[ToolType.FILE_BROWSER]}
                element={
                  <DataManagement
                    dataManagementJobs={sDataManagementJobs}
                    jobsLoading={sJobsLoading}
                    tabVisible={sSelectedTool === ToolType.FILE_BROWSER}
                  />
                }
              />
              {/* The notebook tab */}
              <Route
                path={toolTypeToPath[ToolType.NOTEBOOK]}
                element={<InteractiveSessions type="notebooks" />}
              />
              {/* The visualisation tab */}
              <Route
                path={toolTypeToPath[ToolType.VISUALISE_DATA]}
                element={<InteractiveSessions type="visualisation" />}
              />
              {/* The virtual desktop tab */}
              <Route
                path={toolTypeToPath[ToolType.VIRTUAL_DESKTOPS]}
                element={<InteractiveSessions type="virtual-desktops" />}
              />
              {/* The user-management tab */}
              <Route
                path={toolTypeToPath[ToolType.USER_MANAGEMENT]}
                element={
                  <UserManagement
                    tabVisible={sSelectedTool === ToolType.USER_MANAGEMENT}
                  />
                }
              />
              {/* The notifications tab */}
              <Route
                path={toolTypeToPath[ToolType.VIEW_NOTIFICATIONS]}
                element={<ViewNotifications notifications={sNotifications} />}
              />
              {/* The logs tab */}
              <Route
                path={toolTypeToPath[ToolType.LOGS]}
                element={<Logs entries={sGatewayLogs} focusRow={refLogsFocusRow} />}
              />
              {/* For debugging only - display the data on the currently logged in user */}
              {sSelectedTool === ToolType.OIDC_TOKENS && (
                <Route
                  path="/developer-tokens"
                  element={<UserControlOIDCToken key={sG_LoggedInRefreshTrigger} />}
                />
              )}
              {/* The user guide */}
              <Route
                path={toolTypeToPath[ToolType.USER_GUIDE]}
                element={
                  <div
                    className="panel-main text-colour"
                    style={{
                      flex: '1 1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    Coming soon.....
                  </div>
                }
              />
              {/* Search software */}
              <Route
                path={toolTypeToPath[ToolType.SEARCH_SOFTWARE]}
                element={
                  <div
                    className="panel-main text-colour"
                    style={{
                      flex: '1 1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    Coming soon.....
                  </div>
                }
              />
              {/* The user profile */}
              <Route
                path={toolTypeToPath[ToolType.USER_PROFILE]}
                element={<UserProfile />}
              />
              <Route path="/auth/callback" element={<AuthCallback />} />
              {/* If none of the above then we display an empty page */}
              <Route
                path="*"
                element={
                  <div
                    style={{
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div>
                      <h1
                        className="empty-window"
                        style={{
                          display: 'inline-block',
                          margin: '0px 20px 0px 0px',
                          padding: '0px 23px 0px 0px',
                          fontSize: '24px',
                          fontWeight: '500',
                          verticalAlign: 'top',
                          lineHeight: '49px',
                        }}
                      >
                        404
                      </h1>
                      <div style={{ display: 'inline-block' }}>
                        <h2
                          style={{
                            fontSize: '14px',
                            fontWeight: '400',
                            lineHeight: '49px',
                            margin: '0px',
                          }}
                        >
                          This page could not be found.
                        </h2>
                      </div>
                    </div>
                  </div>
                }
              />
            </Routes>

            {/*
            ///  display all the interactive sessions, but keep them hidden unless they
            ///  are currently selected by the user.
            */}
            <InteractiveSessionsWindow />

            {/* Session Toast */}
            <ToastContainer
              position="top-right"
              autoClose={false}
              hideProgressBar
              closeOnClick={false}
              pauseOnHover
              draggable={false}
              theme={sG_DarkMode ? 'dark' : 'light'}
            />
          </Col>
        </Row>
        <Row style={{ flexShrink: 0 }}>
          <Col style={{ padding: '0px' }}>
            <div className="footer" style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                role="contentinfo"
                aria-label="Science Gateway version"
                className="footer-content"
              >
                <p style={{ marginBottom: '0' }}>SRCNet Science Gateway v0.2.1</p>
              </div>
              <div className="footer-gradient-bar"></div>
            </div>
          </Col>
        </Row>

        {/* NewComputeSessionModal */}
        <Modal
          show={sShowNewSessionModal.show}
          onHide={() => dispatch(setShowNewSessionModal({ show: false }))}
          backdrop="static"
          keyboard={false}
          centered
        >
          <div
            className="panel-background"
            style={{
              ...MODAL_STYLE,
              ...{
                minWidth: '1250px',
                width: '1250px',
                maxWidth: '1250px',
                position: 'relative',
                left: '-350px',
                fontSize: sFontSize.toString() + 'pt',
              },
            }}
            data-theme={sG_DarkMode ? 'dark' : 'light'}
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: '1.1em', fontWeight: '800' }}>
                Launch a Compute Session
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <NewSessionPanel
                onClose={() => dispatch(setShowNewSessionModal({ show: false }))}
                site_uuid={sShowNewSessionModal.site_uuid}
                service_uuid={sShowNewSessionModal.service_uuid}
                service_type={sShowNewSessionModal.service_type}
              />
            </Modal.Body>
          </div>
        </Modal>

        {/* Preferences modal */}
        <Modal
          show={sShowPreferencesModal}
          onHide={() => dispatch(setShowPreferencesModal(false))}
          backdrop="static"
          keyboard={false}
          centered
        >
          <div
            className="panel-background"
            style={{
              ...MODAL_STYLE,
              ...{
                minWidth: '1250px',
                width: '1250px',
                maxWidth: '1250px',
                position: 'relative',
                left: '-350px',
                fontSize: sFontSize.toString() + 'pt',
              },
            }}
            data-theme={sG_DarkMode ? 'dark' : 'light'}
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: '1.1em', fontWeight: '800' }}>
                {t('User preferences')}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Preferences
                onClose={() => dispatch(setShowPreferencesModal(false))}
                projects={sProjects}
              />
            </Modal.Body>
          </div>
        </Modal>
      </Container>
    </Suspense>
  );
} // App

export default App;
